import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  api,
  formatBRL,
  formatBRLParts,
  formatDateTime,
  type Conversion,
  type Overview,
  type ValidatedRow,
  type DiaDoRelatorio,
} from '../api.js';
import { PageHead } from './PageHead.js';
import { DailyChart } from './DailyChart.js';

const CHANNEL_LABEL: Record<string, { icon: string; label: string }> = {
  instagram: { icon: '📷', label: 'Instagram' },
  tiktok: { icon: '🎵', label: 'TikTok' },
  whatsapp: { icon: '💬', label: 'WhatsApp' },
  // Link copiado à mão pelo botão "Link" na aba Ofertas.
  manual: { icon: '🔗', label: 'Link manual' },
};

const STATUS_BADGE: Record<string, string> = {
  COMPLETED: 'ok',
  PENDING: 'warn',
  UNPAID: 'warn',
  CANCELLED: 'err',
  PARTIAL_COMPLETED: 'warn',
};

/**
 * O que a Shopee chama × o que a pessoa reconhece.
 *
 * COMPLETED/PENDING são nomes de campo da API, não vocabulário de quem vende.
 * Sem tradução a tela expõe a estrutura do sistema em vez do estado do pedido.
 */
const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'pago',
  PENDING: 'em validação',
  UNPAID: 'não pago',
  CANCELLED: 'cancelado',
  PARTIAL_COMPLETED: 'parcial',
};

function channelInfo(channel: string | null) {
  if (!channel) return { icon: '❓', label: 'Sem sub-ID' };
  return CHANNEL_LABEL[channel] ?? { icon: '🔗', label: channel };
}

const RANGES = [7, 30, 90] as const;

export function Performance() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [days, setDays] = useState<number>(30);
  const [serie, setSerie] = useState<DiaDoRelatorio[]>([]);
  const [channelFilter, setChannelFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // A Shopee exige >30s entre consultas de primeira pagina do relatorio (o
  // cursor scrollId expira). Em vez de deixar o usuario tomar erro, o botao
  // fica desabilitado com contagem. Nao vale para a busca de produtos, que
  // nao tem essa restricao.
  const [cooldown, setCooldown] = useState(0);
  const [validated, setValidated] = useState<{
    rows: ValidatedRow[];
    totalCommissionCents: number;
  } | null>(null);
  const [loadingValidated, setLoadingValidated] = useState(false);
  const [message, setMessage] = useState<{ kind: 'info' | 'err' | 'warn'; text: string } | null>(
    null,
  );

  const load = useCallback(async (range: number, channel: string | null) => {
    setLoading(true);
    try {
      const [ov, convs, di] = await Promise.all([
        api.overview(range),
        api.conversions({ days: range, ...(channel ? { channel } : {}) }),
        // Falha do gráfico não derruba os totais: eles são a informação
        // principal da tela, o gráfico é leitura complementar.
        api.daily(range).catch(() => ({ days: range, series: [] as DiaDoRelatorio[] })),
      ]);
      setOverview(ov);
      setConversions(convs);
      setSerie(di.series);
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao carregar o desempenho',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days, channelFilter);
  }, [load, days, channelFilter]);

  // Deriva a contagem da ultima sincronizacao: sobrevive a troca de aba e a
  // recarregar a pagina, porque a fonte da verdade e o servidor.
  useEffect(() => {
    const startedAt = overview?.lastSync?.startedAt;
    if (!startedAt) return;
    const readyAt = new Date(startedAt).getTime() + 31_000;

    const update = () => {
      const left = Math.ceil((readyAt - Date.now()) / 1000);
      setCooldown(left > 0 ? left : 0);
      return left;
    };
    if (update() <= 0) return;

    const timer = setInterval(() => {
      if (update() <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [overview?.lastSync?.startedAt]);

  async function sync() {
    setSyncing(true);
    setMessage(null);
    try {
      const result = await api.syncConversions(days);
      setMessage({
        kind: result.truncated ? 'warn' : 'info',
        text: result.truncated
          ? `${result.fetched} conversões trazidas, mas o limite de páginas foi atingido — ` +
            `pode haver mais dados. Reduza o período e sincronize de novo.`
          : `${result.fetched} conversões trazidas da Shopee (${result.upserted} gravadas).`,
      });
      setCooldown(31);
      await load(days, channelFilter);
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'A sincronização falhou',
      });
    } finally {
      setSyncing(false);
    }
  }

  /**
   * Comissao validada e uma consulta pontual (nao persistimos): e o extrato do
   * que sera pago. Carregada sob demanda para nao gastar chamada da Shopee em
   * quem so quer ver o painel.
   */
  async function loadValidated() {
    setLoadingValidated(true);
    setMessage(null);
    try {
      const result = await api.validated(200);
      setValidated(result);
      if (result.rows.length === 0) {
        setMessage({
          kind: 'info',
          text: 'Nenhuma comissão validada ainda. A Shopee valida depois que o pedido fecha.',
        });
      }
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao buscar a comissão validada',
      });
    } finally {
      setLoadingValidated(false);
    }
  }

  const totals = overview?.totals;
  const maxCommission = Math.max(
    1,
    ...(overview?.byChannel.map((c) => c.totalCommissionCents) ?? [1]),
  );

  return (
    <>
      <PageHead
        eyebrow={`Desempenho · ${days} dias`}
        title="Quanto isso rendeu"
        description="Comissão real vinda do relatório de conversão da Shopee, atribuída por canal através do sub-ID que colocamos em cada link."
        readout={[
          totals && {
            label: 'de comissão',
            value: formatBRL(totals.totalCommissionCents),
            tom: totals.totalCommissionCents > 0 ? ('gain' as const) : ('quiet' as const),
          },
          totals && { label: 'conversões', value: totals.conversions },
          // Cancelada é dinheiro que não entra. Some da fileira quando é zero:
          // um zero permanente ensina a ignorar o número.
          totals && totals.cancelled > 0
            ? { label: 'canceladas', value: totals.cancelled, tom: 'loss' as const }
            : null,
        ]}
        actions={
          <>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className="tab"
              aria-current={days === r ? 'page' : undefined}
              onClick={() => setDays(r)}
            >
              {r} dias
            </button>
          ))}
          <button
            type="button"
            className="primary"
            onClick={sync}
            disabled={syncing || cooldown > 0}
            title={
              cooldown > 0
                ? 'A Shopee exige mais de 30s entre consultas ao relatório de conversão'
                : 'Buscar as conversões na Shopee'
            }
          >
            {syncing ? (
              <span className="spinner" />
            ) : cooldown > 0 ? (
              `Aguarde ${cooldown}s`
            ) : (
              'Sincronizar'
            )}
          </button>
          </>
        }
      />

      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

      {totals && totals.cancelled > 0 && (
        <div className="notice warn">
          {totals.cancelled} conversão(ões) cancelada(s) no período. Elas não entram na comissão
          nem no valor vendido — pedido cancelado não gera receita.
        </div>
      )}

      {overview?.lastSync && (
        <p className="muted" style={{ marginTop: 0 }}>
          Última sincronização: {formatDateTime(overview.lastSync.startedAt)}
          {overview.lastSync.ok
            ? ` · ${overview.lastSync.fetched} conversões`
            : ` · falhou: ${overview.lastSync.error ?? 'erro'}`}
        </p>
      )}

      {loading && !overview ? (
        <div className="empty">
          <span className="spinner" /> Carregando…
        </div>
      ) : totals && totals.conversions === 0 ? (
        <div className="card empty">
          <strong>Nenhuma conversão neste período</strong>
          Clique em “Sincronizar” para buscar o relatório na Shopee. Só aparecem vendas vindas de
          links gerados por aqui.
        </div>
      ) : (
        totals && (
          <>
            {/* Totais */}
            <div className="stats">
              <Stat label="Comissão bruta" money={totals.totalCommissionCents} primary />
              <Stat label="Comissão líquida" money={totals.netCommissionCents} />
              <Stat label="Valor vendido" money={totals.purchaseValueCents} />
              <Stat label="Conversões" value={String(totals.conversions)} />
              <Stat label="Itens vendidos" value={String(totals.items)} />
              {totals.cancelled > 0 && (
                <Stat
                  label="Canceladas"
                  value={String(totals.cancelled)}
                  foot={<span className="loss">fora do total</span>}
                />
              )}
            </div>

            {/* O gráfico vem DEPOIS dos totais: o número responde "quanto", o
                gráfico responde "quando" — e a segunda pergunta só interessa
                depois da primeira. */}
            <DailyChart series={serie} dias={days} />

            {/* Por canal */}
            <section className="card" style={{ padding: 18, marginBottom: 18 }}>
              <p className="section-title">Comissão por canal</p>
              <div style={{ display: 'grid', gap: 10 }}>
                {overview.byChannel.map((c) => {
                  const info = channelInfo(c.channel);
                  const pct = (c.totalCommissionCents / maxCommission) * 100;
                  const active = channelFilter === c.channel;
                  const semDados = c.conversions === 0;
                  return (
                    <button
                      key={c.channel ?? 'none'}
                      type="button"
                      onClick={() => setChannelFilter(active ? null : c.channel)}
                      title={active ? 'Remover filtro' : 'Filtrar conversões por este canal'}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '140px 1fr auto',
                        gap: 12,
                        alignItems: 'center',
                        border: `1px solid ${active ? 'var(--line-strong)' : 'transparent'}`,
                        background: active ? 'var(--surface-2)' : 'transparent',
                        borderRadius: 'var(--r-control)',
                        padding: '8px 10px',
                        textAlign: 'left',
                        opacity: semDados && !active ? 0.55 : 1,
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {info.icon} {info.label}
                      </span>
                      {/* Barra proporcional: comparar canais lado a lado é mais
                          rápido de ler do que só os números. */}
                      <span
                        style={{
                          height: 9,
                          borderRadius: 5,
                          background: 'var(--surface-2)',
                          overflow: 'hidden',
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            height: '100%',
                            width: `${pct}%`,
                            // Verde porque é dinheiro entrando; cheio só no
                            // líder. Quatro barras verdes idênticas não diziam
                            // nada — todas são ganho. O que a pessoa quer saber
                            // é ONDE vale investir esforço.
                            background: semDados
                              ? 'var(--line-strong)'
                              : c.totalCommissionCents === maxCommission
                                ? 'var(--gain)'
                                : 'color-mix(in srgb, var(--gain) 42%, transparent)',
                            borderRadius: 5,
                          }}
                        />
                      </span>
                      <span style={{ textAlign: 'right' }}>
                        <strong style={{ fontSize: 13 }}>
                          {formatBRL(c.totalCommissionCents)}
                          {/* Pista redundante: o líder não pode ser
                              identificado só pela cor da barra — quem não
                              distingue verde perderia a informação. */}
                          {!semDados && c.totalCommissionCents === maxCommission && (
                            <span className="badge ok" style={{ marginLeft: 6 }}>
                              maior
                            </span>
                          )}
                        </strong>
                        <span className="muted" style={{ display: 'block' }}>
                          {semDados
                            ? 'sem conversão no período'
                            : `${c.conversions} conv · ${formatBRL(c.purchaseValueCents)} vendidos`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Top produtos */}
            {overview.byProduct.length > 0 && (
              <section className="card" style={{ padding: 18, marginBottom: 18 }}>
                <p className="section-title">Produtos que mais venderam</p>
                <div style={{ display: 'grid', gap: 8 }}>
                  {overview.byProduct.slice(0, 10).map((p) => (
                    <div
                      key={`${p.shopeeItemId}-${p.productId}`}
                      style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                    >
                      {p.imageUrl ? (
                        <img
                          src={p.imageUrl}
                          alt=""
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 6,
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 6,
                            background: 'var(--surface-2)',
                          }}
                        />
                      )}
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                        {p.name ?? `Item ${p.shopeeItemId}`}
                        {/* Distinguir o que veio do nosso catálogo importa: sem
                            isso não se sabe se a venda veio do nosso post. */}
                        {!p.productId && (
                          <span className="muted"> · fora do catálogo daqui</span>
                        )}
                      </span>
                      <span className="muted">{p.qty}x</span>
                      <strong style={{ fontSize: 12.5, minWidth: 90, textAlign: 'right' }}>
                        {formatBRL(p.purchaseValueCents)}
                      </strong>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Comissao validada */}
            <section className="card" style={{ padding: 18, marginBottom: 18 }}>
              <div
                style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
              >
                <p className="section-title" style={{ margin: 0 }}>
                  Comissão validada
                </p>
                {validated && (
                  <strong style={{ fontSize: 15, color: 'var(--ok)' }}>
                    {formatBRL(validated.totalCommissionCents)}
                  </strong>
                )}
                <button
                  type="button"
                  className="ghost"
                  onClick={() => void loadValidated()}
                  disabled={loadingValidated}
                  style={{ marginLeft: 'auto' }}
                >
                  {loadingValidated ? (
                    <span className="spinner" />
                  ) : validated ? (
                    '↻ atualizar'
                  ) : (
                    'Consultar'
                  )}
                </button>
              </div>
              <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
                O que a Shopee já validou e vai pagar — diferente da comissão bruta acima, que
                inclui pedidos ainda em validação.
              </p>

              {validated && validated.rows.length > 0 && (
                <div style={{ display: 'grid', gap: 6, marginTop: 14 }}>
                  {validated.rows.slice(0, 12).map((row, i) => {
                    const info = channelInfo(row.channel);
                    return (
                      <div
                        key={row.conversionId ?? i}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'center',
                          fontSize: 12.5,
                          paddingTop: 6,
                          borderTop: '1px solid var(--border)',
                        }}
                      >
                        <span className="badge accent">
                          {info.icon} {info.label}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {row.itemNames[0] ?? `Conversão ${row.conversionId}`}
                          {row.itemNames.length > 1 && (
                            <span className="muted"> +{row.itemNames.length - 1}</span>
                          )}
                        </span>
                        <span className="muted">{formatDateTime(row.purchaseTime)}</span>
                        <strong style={{ minWidth: 78, textAlign: 'right', color: 'var(--ok)' }}>
                          {formatBRL(row.netCommissionCents)}
                        </strong>
                      </div>
                    );
                  })}
                  {validated.rows.length > 12 && (
                    <span className="muted">
                      … e mais {validated.rows.length - 12} registro(s)
                    </span>
                  )}
                </div>
              )}
            </section>

            {/* Conversões */}
            <section className="card">
              <div
                style={{
                  padding: '13px 16px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <p className="section-title" style={{ margin: 0 }}>
                  Conversões {channelFilter ? `— ${channelInfo(channelFilter).label}` : ''}
                </p>
                {channelFilter && (
                  <button type="button" className="ghost" onClick={() => setChannelFilter(null)}>
                    limpar filtro
                  </button>
                )}
              </div>

              {conversions.length === 0 ? (
                <div className="empty">Nenhuma conversão neste filtro.</div>
              ) : (
                conversions.map((conv) => {
                  const info = channelInfo(conv.channel);
                  const statusCls = conv.orderStatus
                    ? (STATUS_BADGE[conv.orderStatus] ?? '')
                    : '';
                  return (
                    <div key={conv.id} className="post-row">
                      {conv.items[0]?.imageUrl ? (
                        <img src={conv.items[0].imageUrl} alt="" className="post-thumb" />
                      ) : (
                        <div className="post-thumb" />
                      )}
                      <div className="post-main">
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <strong style={{ fontSize: 13 }}>
                            {conv.items[0]?.itemName ?? `Conversão ${conv.conversionId}`}
                          </strong>
                          <span className="badge">
                            {info.icon} {info.label}
                          </span>
                          {conv.orderStatus && (
                            <span className={`badge ${statusCls}`}>
                              {STATUS_LABEL[conv.orderStatus] ?? conv.orderStatus.toLowerCase()}
                            </span>
                          )}
                          {conv.buyerType && (
                            <span className="badge">
                              {conv.buyerType === 'NEW' ? 'novo comprador' : 'recorrente'}
                            </span>
                          )}
                        </div>
                        <div className="muted" style={{ marginTop: 4 }}>
                          🕑 {formatDateTime(conv.purchaseTime)} · {conv.itemCount} item(ns)
                          {conv.device ? ` · ${conv.device}` : ''}
                          {conv.items.length > 1 ? ` · +${conv.items.length - 1} produto(s)` : ''}
                        </div>
                      </div>
                      <div className="post-side">
                        <strong
                          style={{
                            fontSize: 13,
                            // Pedido cancelado rende zero: em tinta discreta a
                            // linha para de parecer receita.
                            color:
                              conv.totalCommissionCents > 0 ? 'var(--ink)' : 'var(--ink-3)',
                          }}
                        >
                          {formatBRL(conv.totalCommissionCents)}
                        </strong>
                        <span className="muted">
                          venda {formatBRL(conv.purchaseValueCents)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </section>
          </>
        )
      )}
    </>
  );
}

/**
 * Cartão de métrica.
 *
 * `money` em centavos separa a moeda dos dígitos, para os números alinharem
 * entre cartões. `primary` marca o principal com um fio lateral no acento — e
 * não pintando o número de laranja: número grande em cor de marca vira enfeite
 * e some com o significado de "este é o que importa".
 */
function Stat({
  label,
  value,
  money,
  primary,
  foot,
}: {
  label: string;
  value?: string;
  money?: number;
  primary?: boolean;
  foot?: ReactNode;
}) {
  const partes = money !== undefined ? formatBRLParts(money) : null;
  return (
    <div className={`stat${primary ? ' is-primary' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {partes ? (
          <>
            <span className="stat-unit">{partes.unit}</span>
            {partes.digits}
          </>
        ) : (
          value
        )}
      </div>
      {foot && <div className="stat-foot">{foot}</div>}
    </div>
  );
}
